import React, { useState, useEffect } from 'react';
import { useAuth, authFetch } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailSettings() {
  const { user } = useAuth();
  const [senderStatus, setSenderStatus] = useState(null);
  const [digestHistory, setDigestHistory] = useState([]);
  const [nextRun, setNextRun] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [resending, setResending] = useState(false);

  const fetchData = async () => {
    try {
      const [sRes, dRes, nRes] = await Promise.all([
        authFetch(`${API}/api/email/sender-status`),
        authFetch(`${API}/api/digest/history`),
        authFetch(`${API}/api/digest/next-run`),
      ]);
      if (sRes.ok) setSenderStatus(await sRes.json());
      if (dRes.ok) setDigestHistory((await dRes.json()).history || []);
      if (nRes.ok) setNextRun(await nRes.json());
    } catch (e) {}
  };
  useEffect(() => { fetchData(); }, [triggerResult, verifyResult]);

  const triggerDigest = async () => { setTriggering(true); setTriggerResult(null); try { setTriggerResult(await (await authFetch(`${API}/api/digest/trigger`, { method: 'POST' })).json()); } catch (e) { setTriggerResult({ error: e.message }); } finally { setTriggering(false); } };
  const initiateVerification = async () => { setVerifying(true); setVerifyResult(null); try { setVerifyResult(await (await authFetch(`${API}/api/email/initiate-verification`, { method: 'POST' })).json()); } catch (e) { setVerifyResult({ status: 'error', message: e.message }); } finally { setVerifying(false); setTimeout(fetchData, 2000); } };
  const resendVerification = async () => { setResending(true); setVerifyResult(null); try { setVerifyResult(await (await authFetch(`${API}/api/email/resend-verification`, { method: 'POST' })).json()); } catch (e) { setVerifyResult({ status: 'error', message: e.message }); } finally { setResending(false); } };

  const isSuperAdmin = user?.role === 'super_admin';
  const isVerified = senderStatus?.verified;
  const hasSenders = senderStatus?.senders?.length > 0;

  return (
    <div className="bg-tf-surface border border-tf-primary" data-testid="email-settings">
      <div className="px-5 py-4 border-b border-tf-primary">
        <h2 className="font-heading font-bold text-sm tracking-[0.2em] uppercase text-tf-text">Email & Digest</h2>
      </div>
      <div className="px-5 py-4 border-b border-tf-border-muted">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-2.5 h-2.5 rounded-full ${isVerified ? 'bg-tf-success' : senderStatus?.configured ? 'bg-tf-warning' : 'bg-tf-danger'}`} />
          <span className="font-mono text-[10px] font-bold text-tf-text">{isVerified ? 'Sender Verified' : senderStatus?.configured ? 'Verification Needed' : 'Not Configured'}</span>
        </div>
        <p className="font-mono text-[10px] text-tf-text-2">Sender: {senderStatus?.sender_email || 'N/A'}</p>
        {hasSenders && <div className="mt-2 space-y-1">{senderStatus.senders.map((s, i) => (
          <div key={i} className="flex items-center gap-2"><div className={`w-1.5 h-1.5 rounded-full ${s.verified ? 'bg-tf-success' : 'bg-tf-warning animate-pulse-dot'}`} /><span className="font-mono text-[10px] text-tf-text-2">{s.from_email} — {s.verified ? 'Verified' : 'Pending'}</span></div>
        ))}</div>}
        {isSuperAdmin && !isVerified && senderStatus?.configured && (
          <div className="flex gap-2 mt-3">
            {!hasSenders && <button data-testid="initiate-verification-btn" onClick={initiateVerification} disabled={verifying} className="px-3 py-1.5 bg-tf-accent text-white font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-tf-accent-hover disabled:opacity-50">{verifying ? 'Sending...' : 'Initiate Verification'}</button>}
            {hasSenders && <button data-testid="resend-verification-btn" onClick={resendVerification} disabled={resending} className="px-3 py-1.5 border border-tf-accent text-tf-accent font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-tf-accent hover:text-white disabled:opacity-50">{resending ? 'Resending...' : 'Resend Verification'}</button>}
          </div>
        )}
        {verifyResult && <p className={`font-mono text-[10px] mt-2 ${verifyResult.status === 'error' ? 'text-tf-danger' : 'text-tf-accent'}`}>{verifyResult.message || verifyResult.status}</p>}
      </div>
      <div className="px-5 py-4 border-b border-tf-border-muted">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono text-[10px] font-bold text-tf-text">Weekly Digest</span>
          <div className="flex items-center gap-1.5"><div className={`w-2 h-2 rounded-full ${nextRun?.active ? 'bg-tf-success animate-pulse-dot' : 'bg-tf-text-3'}`} /><span className="font-mono text-[9px] text-tf-text-2">{nextRun?.active ? 'Scheduled' : 'Inactive'}</span></div>
        </div>
        {nextRun?.next_run && <p className="font-mono text-[10px] text-tf-text-2">Next: {new Date(nextRun.next_run).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</p>}
        {isSuperAdmin && <button data-testid="trigger-digest-btn" onClick={triggerDigest} disabled={triggering} className="mt-3 px-3 py-1.5 bg-tf-accent text-white font-mono text-[9px] tracking-[0.15em] uppercase hover:bg-tf-accent-hover disabled:opacity-50">{triggering ? 'Sending...' : 'Send Digest Now'}</button>}
        {triggerResult && <p className={`font-mono text-[10px] mt-2 ${triggerResult.error ? 'text-tf-danger' : 'text-tf-success'}`}>{triggerResult.error ? `Error: ${triggerResult.error}` : `Sent ${triggerResult.result?.sent || 0}/${triggerResult.result?.total || 0}`}</p>}
      </div>
      {digestHistory.length > 0 && <div className="px-5 py-3">
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-tf-text-2 block mb-2">Recent Digests</span>
        {digestHistory.slice(0, 5).map((log, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 border-b border-tf-border-muted last:border-b-0">
            <span className="font-mono text-[10px] text-tf-text-2">{new Date(log.sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            <span className="font-mono text-[10px] text-tf-text">{log.sent_count}/{log.total_users}</span>
          </div>
        ))}
      </div>}
    </div>
  );
}
